import MapboxDraw, { DrawModeChangeEvent } from "@mapbox/mapbox-gl-draw";
import { Feature, Polygon } from "geojson";
import { defer, uniqBy } from "lodash";
import { LngLatLike } from "mapbox-gl";

import { bbox, booleanContains, booleanIntersects, booleanPointInPolygon } from "fond/turf";
import { isAnyPolygon, isLineString, isPoint } from "fond/types/geojson";

import { isFeatureInEditableScope } from "./helper";

/**
 * Selects all draw features that fall within or intersect the given polygon,
 * then transitions to simple_select with those features selected.
 *
 * Shared by polygon_select and lasso_select modes.
 */
export function selectFeaturesInPolygon(ctx: MapboxDraw.DrawCustomModeThis, polygon: Feature<Polygon>): void {
  if (!ctx.map) return;

  // Get the bounding box of the polygon
  // and convert it to a bounding box in pixel coordinates
  // to use with queryRenderedFeatures
  const polygonBounds = bbox(polygon);
  const southWest = [polygonBounds[0], polygonBounds[1]] as LngLatLike;
  const northEast = [polygonBounds[2], polygonBounds[3]] as LngLatLike;
  const northEastPointPixel = ctx.map.project(northEast);
  const southWestPointPixel = ctx.map.project(southWest);

  // Get all features within the bounding box
  // and filter them to only include those that are within the polygon
  const features = uniqBy(
    ctx.map
      ?.queryRenderedFeatures([southWestPointPixel, northEastPointPixel], {
        layers: [
          // Select all features within the drawControl styles
          ...(ctx._ctx.options.styles?.map(({ id }) => id) || []),
        ],
      })
      .map((f) => {
        // We need to get the feature from the drawControl, we cannot rely on the geometry within the feature
        // returned from the click function above.
        const drawnFeature = ctx.getFeature(f.properties?.id)?.toGeoJSON() || f;
        return drawnFeature as Feature;
      })
      .filter((f) => {
        if (f.id || f.properties?.id !== undefined) {
          // If editingLayerIds is defined, only select features from editable layers
          if (ctx._ctx.options.editOptions?.editingLayerIds && "properties" in f) {
            const { editingLayerIds, editableSublayers = [] } = ctx._ctx.options.editOptions;

            // Use shared helper to check if feature is in editable scope
            if (!isFeatureInEditableScope(f, editingLayerIds, editableSublayers)) {
              return false;
            }
          }
          // check if feature is within or intersects the polygon
          if (isPoint(f)) {
            return booleanPointInPolygon(f, polygon);
          } else if (isLineString(f) || isAnyPolygon(f)) {
            return booleanContains(polygon, f) || booleanIntersects(f, polygon);
          }
        }
        return false;
      }),
    (f) => f.id ?? f.properties?.id
  );

  defer(() => {
    ctx.changeMode("simple_select", { featureIds: features.map((f) => f.id ?? f.properties?.id) });
    ctx.map?.fire("draw.modechange", { type: "draw.modechange", mode: "simple_select" } as DrawModeChangeEvent);
    ctx.map?.fire("draw.selectionchange", { features });
  });
}
